import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Calculator,
  CheckCircle2,
  CircleSlash,
  Link2,
  Pencil,
  Plus,
  Store,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Badge,
  Button,
  EmptyState,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { ChannelConnection } from '../components/channel-connection.tsx'
import { ChannelForm } from '../components/channel-form.tsx'
import { ChannelListingForm } from '../components/channel-listing-form.tsx'
import { TabHeader } from '../components/config-tabs.tsx'
import { ListingDraftForm } from '../components/listing-draft-form.tsx'
import { ListingPriceDialog } from '../components/listing-price-dialog.tsx'
import { LoadFailed } from '../components/load-failed.tsx'
import {
  fetchChannelListings,
  fetchPublishable,
  fetchSalesChannels,
  setChannelListingStatus,
  type ChannelListingSummaryType,
  type SalesChannelSummaryType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * Canais — where the shop sells besides its own counter (§23, F030).
 *
 * Two lists, and the second one is the point. A channel is a handful of
 * settings; the LISTINGS are the mapping between what this shop calls a
 * product and what a marketplace calls an advert, and getting one of those
 * wrong ships the wrong thing to a stranger.
 *
 * Duas colunas desta tabela são CALCULADAS e não lidas, pelo mesmo motivo:
 * gravadas, elas mentiriam em silêncio.
 *
 * - **Estoque publicável** vem do saldo das filiais menos o buffer, e mostra o
 *   PORQUÊ ao lado do número: "esgotado" e "o buffer segurou" são idênticos num
 *   marketplace e têm consertos opostos.
 * - **Prontidão** (F110) vem da ficha do produto e da política de preço do
 *   canal. Ela separa o que uma pessoa resolve aprovando do que ela resolve
 *   consertando o produto — misturar os dois produz uma fila que ninguém
 *   revisa, porque metade dela não tem botão que resolva.
 */

/**
 * A prontidão de um anúncio, como um marcador (F110).
 *
 * Texto primeiro, ícone junto, cor por último — a UI_UX proíbe estado
 * dependente só de cor. E os três estados dizem coisas diferentes sobre QUEM
 * age: publicável não precisa de ninguém, "para revisar" precisa de alguém que
 * aprove, e bloqueado precisa de alguém que conserte o produto.
 */
const READINESS: Record<
  string,
  { label: string; tone: 'success' | 'warning' | 'danger'; icon: ReactNode }
> = {
  publishable: {
    label: 'Pode publicar',
    tone: 'success',
    icon: <CheckCircle2 aria-hidden="true" className="size-3" />,
  },
  needs_review: {
    label: 'Para revisar',
    tone: 'warning',
    icon: <AlertTriangle aria-hidden="true" className="size-3" />,
  },
  blocked: {
    label: 'Bloqueado',
    tone: 'danger',
    icon: <CircleSlash aria-hidden="true" className="size-3" />,
  },
}

const KIND_LABELS: Record<string, string> = {
  mercado_livre: 'Mercado Livre',
  shopee: 'Shopee',
  ecommerce: 'E-commerce',
  bling: 'Bling',
  other: 'Outro',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Ativo',
  paused: 'Pausado',
  archived: 'Arquivado',
}

const LISTING_STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicado',
  paused: 'Pausado',
  unlisted: 'Fora do ar',
}

/** Why the publishable figure is what it is, in words a shopkeeper can act on. */
const LIMIT_LABELS: Record<string, string> = {
  stock: 'pelo estoque',
  buffer: 'pelo buffer',
  ceiling: 'pelo teto',
  'no-branches': 'nenhuma filial escolhida',
}

/** A quantity as a person reads it: `3.000000` is three. */
const asQuantity = (value: string): string => {
  const [whole = '0', fraction = ''] = value.split('.')
  const trimmed = fraction.replace(/0+$/, '')
  return trimmed === '' ? whole : `${whole},${trimmed}`
}

const PublishableCell = ({
  channelId,
  listingId,
}: {
  channelId: string
  listingId: string
}): ReactNode => {
  const query = useQuery({
    queryKey: ['channel-publishable', channelId, listingId],
    queryFn: async ({ signal }) =>
      await fetchPublishable(channelId, listingId, signal),
  })

  if (query.isPending) return <Spinner />
  if (query.data === undefined) return <span>—</span>

  return (
    <span className="tabular-nums">
      {asQuantity(query.data.quantity)}{' '}
      <span className="text-(length:--text-xs) text-(--text-muted)">
        {LIMIT_LABELS[query.data.limitedBy] ?? ''}
      </span>
    </span>
  )
}

export const CanaisPage = (): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()
  const canWrite = allows('channel.write')

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [channelFormOpen, setChannelFormOpen] = useState(false)
  const [editing, setEditing] = useState<SalesChannelSummaryType | undefined>(
    undefined
  )
  const [listingFormOpen, setListingFormOpen] = useState(false)
  /* Qual anúncio está com a ficha aberta, e qual está com o cálculo. */
  const [editingDraft, setEditingDraft] =
    useState<ChannelListingSummaryType | null>(null)
  const [pricing, setPricing] = useState<ChannelListingSummaryType | null>(null)

  const channels = useQuery({
    queryKey: ['sales-channels'],
    queryFn: async ({ signal }) => await fetchSalesChannels(undefined, signal),
  })

  const rows = channels.data?.channels ?? []
  // The selection follows the data rather than being stored twice: a channel
  // that was archived elsewhere must not leave the page pointing at nothing.
  const selected = rows.find((row) => row.id === selectedId) ?? rows[0] ?? null

  const listings = useQuery({
    queryKey: ['channel-listings', selected?.id],
    queryFn: async ({ signal }) =>
      selected === null
        ? { listings: [] }
        : await fetchChannelListings(selected.id, signal),
    enabled: selected !== null,
  })

  const changeStatus = useMutation({
    mutationFn: async (input: {
      listingId: string
      status: 'draft' | 'published' | 'paused' | 'unlisted'
      version: number
    }) => {
      if (selected === null) return
      await setChannelListingStatus(selected.id, input.listingId, {
        status: input.status,
        version: input.version,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['channel-listings', selected?.id],
      })
    },
  })

  return (
    <>
      <TabHeader
        title="Canais"
        description="Onde a loja vende além do próprio balcão, e qual produto é qual anúncio."
        actions={
          canWrite ? (
            <Button
              onClick={() => {
                setEditing(undefined)
                setChannelFormOpen(true)
              }}
            >
              <Plus aria-hidden="true" /> Novo canal
            </Button>
          ) : null
        }
      />

      {channels.isPending ? (
        <Spinner />
      ) : channels.isError ? (
        <LoadFailed
          what="os canais"
          onRetry={() => {
            void channels.refetch()
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Store aria-hidden="true" />}
          title="Nenhum canal cadastrado"
          description="Um canal é um marketplace ou loja online. Cadastre um para começar a ligar seus produtos aos anúncios."
        />
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Canal</TableHeaderCell>
                  <TableHeaderCell>Tipo</TableHeaderCell>
                  <TableHeaderCell>Filiais</TableHeaderCell>
                  <TableHeaderCell>Buffer</TableHeaderCell>
                  <TableHeaderCell>Situação</TableHeaderCell>
                  <TableHeaderCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((channel) => (
                  <TableRow
                    key={channel.id}
                    data-selected={channel.id === selected?.id}
                  >
                    <TableCell>
                      <button
                        type="button"
                        className="text-left underline-offset-2 hover:underline"
                        onClick={() => {
                          setSelectedId(channel.id)
                        }}
                      >
                        {channel.name}
                      </button>
                    </TableCell>
                    <TableCell>
                      {KIND_LABELS[channel.kind] ?? channel.kind}
                    </TableCell>
                    <TableCell>
                      {/*
                        Zero is spelled out rather than shown as "0", because a
                        channel with no branch publishes NOTHING and that reads
                        as a misconfiguration people should notice.
                      */}
                      {channel.branchIds.length === 0
                        ? 'nenhuma'
                        : String(channel.branchIds.length)}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {asQuantity(channel.stockBuffer)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        tone={
                          channel.status === 'active' ? 'success' : 'neutral'
                        }
                      >
                        {STATUS_LABELS[channel.status] ?? channel.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {canWrite ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditing(channel)
                            setChannelFormOpen(true)
                          }}
                          aria-label={`Editar ${channel.name}`}
                        >
                          Editar
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {selected === null ? null : (
            <>
              {/* Only where an adapter exists. A "Conectar" button on a channel
                  with nothing behind it is a button that can only disappoint. */}
              {selected.kind === 'mercado_livre' ? (
                <ChannelConnection
                  channelId={selected.id}
                  channelName={selected.name}
                  canWrite={canWrite}
                />
              ) : null}

              <TabHeader
                title={`Anúncios de ${selected.name}`}
                description="Qual produto do Aeris corresponde a qual anúncio no canal. Um produto tem um anúncio por canal, e vice-versa."
                actions={
                  canWrite ? (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setListingFormOpen(true)
                      }}
                    >
                      <Link2 aria-hidden="true" /> Ligar produto
                    </Button>
                  ) : null
                }
              />

              {listings.isPending ? (
                <Spinner />
              ) : listings.isError ? (
                <LoadFailed
                  what="os anúncios deste canal"
                  onRetry={() => {
                    void listings.refetch()
                  }}
                />
              ) : listings.data.listings.length === 0 ? (
                <EmptyState
                  icon={<Link2 aria-hidden="true" />}
                  title="Nenhum produto ligado"
                  description="Ligue um produto a um anúncio para que este canal saiba o que está vendendo."
                />
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Produto</TableHeaderCell>
                        <TableHeaderCell>SKU</TableHeaderCell>
                        <TableHeaderCell>Anúncio</TableHeaderCell>
                        <TableHeaderCell>Prontidão</TableHeaderCell>
                        <TableHeaderCell>Estoque publicável</TableHeaderCell>
                        <TableHeaderCell>Sincronização</TableHeaderCell>
                        <TableHeaderCell>Situação</TableHeaderCell>
                        <TableHeaderCell>
                          <span className="sr-only">Ações</span>
                        </TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {listings.data.listings.map((listing) => (
                        <TableRow key={listing.id}>
                          <TableCell>
                            <span className="block">{listing.productName}</span>
                            {listing.categoryLabel === null ? null : (
                              <span className="block text-(length:--text-xs) text-(--text-muted)">
                                {listing.categoryLabel}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{listing.sku}</TableCell>
                          <TableCell className="font-(--font-mono)">
                            {listing.externalId ?? (
                              <span className="font-(--font-sans) text-(--text-muted)">
                                Rascunho
                              </span>
                            )}
                            {listing.externalVariationId === null ? null : (
                              <span className="block text-(length:--text-xs) text-(--text-muted)">
                                variação {listing.externalVariationId}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {/*
                              A primeira razão, e não todas: a linha tem espaço
                              para uma frase, e a lista já vem com o que bloqueia
                              na frente. Ver `readiness` em @aeris/channels.
                            */}
                            <div className="flex flex-col gap-1">
                              <Badge
                                tone={
                                  READINESS[listing.readiness]?.tone ??
                                  'neutral'
                                }
                                icon={READINESS[listing.readiness]?.icon}
                              >
                                {READINESS[listing.readiness]?.label ??
                                  listing.readiness}
                              </Badge>
                              {listing.readinessReasons[0] ===
                              undefined ? null : (
                                <span className="text-(length:--text-xs) text-(--text-muted)">
                                  {listing.readinessReasons[0].message}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <PublishableCell
                              channelId={selected.id}
                              listingId={listing.id}
                            />
                          </TableCell>
                          {/*
                            O que a F109 gravou por anúncio e a tela ainda não
                            lia. A mensagem do botão "Sincronizar estoque" manda
                            olhar aqui, e até agora não havia o que olhar.
                          */}
                          <TableCell>
                            {listing.lastError !== null ? (
                              <span className="text-(length:--text-xs) text-(--danger)">
                                {listing.lastError}
                              </span>
                            ) : listing.lastSyncedAt === null ? (
                              <span className="text-(--text-muted)">—</span>
                            ) : (
                              <span className="text-(length:--text-xs) text-(--text-muted)">
                                {new Date(listing.lastSyncedAt).toLocaleString(
                                  'pt-BR'
                                )}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {canWrite ? (
                              <Select
                                aria-label={`Situação de ${listing.productName}`}
                                value={listing.status}
                                onChange={(event) => {
                                  changeStatus.mutate({
                                    listingId: listing.id,
                                    status: event.target.value as 'published',
                                    version: listing.version,
                                  })
                                }}
                              >
                                {Object.entries(LISTING_STATUS_LABELS).map(
                                  ([value, label]) => (
                                    <option key={value} value={value}>
                                      {label}
                                    </option>
                                  )
                                )}
                              </Select>
                            ) : (
                              (LISTING_STATUS_LABELS[listing.status] ??
                              listing.status)
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end gap-1">
                              {canWrite ? (
                                <Button
                                  variant="ghost"
                                  aria-label={`Editar a ficha de ${listing.productName}`}
                                  onClick={() => {
                                    setEditingDraft(listing)
                                  }}
                                >
                                  <Pencil aria-hidden="true" /> Ficha
                                </Button>
                              ) : null}
                              {/*
                                "Ver cálculo" só com categoria: a comissão
                                depende dela, e o botão sem ela só poderia
                                devolver a recusa que a linha já mostra.
                              */}
                              {listing.categoryRef === null ? null : (
                                <Button
                                  variant="ghost"
                                  aria-label={`Ver o cálculo do preço de ${listing.productName}`}
                                  onClick={() => {
                                    setPricing(listing)
                                  }}
                                >
                                  <Calculator aria-hidden="true" /> Ver cálculo
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </>
      )}

      {/* Keyed and conditional: closing the dialog discards the form. */}
      {channelFormOpen ? (
        <ChannelForm
          key={editing?.id ?? 'new'}
          open
          channel={editing}
          onOpenChange={setChannelFormOpen}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: ['sales-channels'],
            })
          }}
        />
      ) : null}

      {/* Montados com `key`: fechar descarta o formulário, e abrir outra linha
          não mostra os valores da anterior por um quadro. */}
      {selected === null || editingDraft === null ? null : (
        <ListingDraftForm
          key={editingDraft.id}
          open
          channelId={selected.id}
          listing={editingDraft}
          onOpenChange={(next) => {
            if (!next) setEditingDraft(null)
          }}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: ['channel-listings', selected.id],
            })
          }}
        />
      )}

      {selected === null || pricing === null ? null : (
        <ListingPriceDialog
          key={pricing.id}
          open
          channelId={selected.id}
          listingId={pricing.id}
          productName={pricing.productName}
          onOpenChange={(next) => {
            if (!next) setPricing(null)
          }}
        />
      )}

      {selected === null || !listingFormOpen ? null : (
        <ChannelListingForm
          open
          channelId={selected.id}
          onOpenChange={setListingFormOpen}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: ['channel-listings', selected.id],
            })
          }}
        />
      )}
    </>
  )
}
